/**
 * MASTERCASE — Scroll Stability & Field Restore  (v3 — robust)
 *
 * Problem: Horizon's media-gallery.js calls this.replaceWith(newGallery)
 * on every variant change. This destroys the gallery DOM and inserts a
 * new one, causing a layout reflow that shifts scroll position.
 * Additionally, html { scroll-behavior: smooth } in base.css makes
 * any corrective scrollTo animate instead of snapping back instantly.
 *
 * Solution — 3 layers:
 *  1. Intercept Element.prototype.replaceWith for media-gallery
 *     → save scrollY, do the replace, instantly restore scrollY
 *  2. Temporarily force scroll-behavior: auto on <html> during
 *     variant changes so scrollTo is synchronous/instant
 *  3. MutationObserver as safety net — watches for gallery DOM
 *     mutations and corrects any residual scroll drift
 *
 * Also preserves MC custom field values across Horizon's morph cycle.
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════
   * LAYER 0 — CSS scroll anchoring hints
   * ═══════════════════════════════════════════════════ */
  var style = document.createElement('style');
  style.textContent = [
    'variant-picker { overflow-anchor: auto; }',
    'media-gallery { overflow-anchor: none; }',
    '.mc-pp-overlay { overflow-anchor: none; }'
  ].join('\n');
  document.head.appendChild(style);

  /* ═══════════════════════════════════════════════════
   * Shared scroll-lock state
   * ═══════════════════════════════════════════════════ */
  var lockedScrollY = null;
  var lockActive = false;
  var lockTimer = null;

  /** Force scroll-behavior: auto and remember position */
  function lockScroll() {
    lockedScrollY = window.scrollY;
    lockActive = true;
    document.documentElement.style.scrollBehavior = 'auto';
    clearTimeout(lockTimer);
  }

  /** Restore scroll instantly, then re-enable smooth after a delay */
  function unlockScroll() {
    if (!lockActive) return;
    if (lockedScrollY !== null) {
      window.scrollTo(0, lockedScrollY);
    }
    lockActive = false;
    /* Keep scroll-behavior: auto for a few frames to absorb any
       async layout shifts, then restore the theme's smooth setting */
    clearTimeout(lockTimer);
    lockTimer = setTimeout(function () {
      document.documentElement.style.scrollBehavior = '';
      lockedScrollY = null;
    }, 300);
  }

  /* ═══════════════════════════════════════════════════
   * LAYER 1 — Intercept replaceWith on <media-gallery>
   * ═══════════════════════════════════════════════════ */
  var _replaceWith = Element.prototype.replaceWith;

  Element.prototype.replaceWith = function () {
    if (this.tagName && this.tagName.toLowerCase() === 'media-gallery') {
      lockScroll();
      _replaceWith.apply(this, arguments);
      /* Restore immediately (synchronous because scroll-behavior: auto) */
      if (lockedScrollY !== null) window.scrollTo(0, lockedScrollY);
      /* Also restore on next frames in case of async reflow */
      requestAnimationFrame(function () {
        if (lockedScrollY !== null) window.scrollTo(0, lockedScrollY);
        requestAnimationFrame(function () {
          unlockScroll();
        });
      });
    } else {
      _replaceWith.apply(this, arguments);
    }
  };

  /* ═══════════════════════════════════════════════════
   * LAYER 2 — Lock scroll on variant change event
   *           (catches cases where replaceWith intercept
   *            doesn't fire, e.g. morph-based updates)
   * ═══════════════════════════════════════════════════ */
  document.addEventListener('change', function (e) {
    if (!e.target.closest('variant-picker')) return;
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'SELECT') return;
    lockScroll();
    /* Safety: auto-unlock after 2s in case no gallery replace happens */
    setTimeout(function () { unlockScroll(); }, 2000);
  }, true);

  /* ═══════════════════════════════════════════════════
   * LAYER 3 — MutationObserver safety net
   *           Watches the product section for gallery
   *           removal/insertion and corrects drift
   * ═══════════════════════════════════════════════════ */
  function setupObserver() {
    var section = document.querySelector('.shopify-section-product-information') ||
                  document.querySelector('[class*="shopify-section"]');
    if (!section) return;

    var observer = new MutationObserver(function (mutations) {
      if (!lockActive) return;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        /* Check if media-gallery was added or removed */
        for (var j = 0; j < m.addedNodes.length; j++) {
          if (m.addedNodes[j].tagName &&
              m.addedNodes[j].tagName.toLowerCase() === 'media-gallery') {
            if (lockedScrollY !== null) window.scrollTo(0, lockedScrollY);
            return;
          }
        }
      }
    });

    observer.observe(section, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupObserver);
  } else {
    setupObserver();
  }

  /* ═══════════════════════════════════════════════════
   * FIELD RESTORE — Save/restore MC custom field values
   * across Horizon's morph cycle
   * ═══════════════════════════════════════════════════ */
  var saved = null;

  function saveFields() {
    saved = {};
    document.querySelectorAll('.mc-custom-property').forEach(function (f) {
      var key = f.getAttribute('data-property-name');
      if (key) saved[key] = f.value || '';
    });
    var pt = document.querySelector('[id^="mc-pp-val-"]');
    if (pt) saved._phoneText = pt.textContent || '';
  }

  function restoreFields() {
    if (!saved) return;
    document.querySelectorAll('.mc-custom-property').forEach(function (f) {
      var key = f.getAttribute('data-property-name');
      if (key && saved[key]) f.value = saved[key];
    });
    var pt = document.querySelector('[id^="mc-pp-val-"]');
    if (pt && saved._phoneText && saved._phoneText !== 'Choisir votre modèle') {
      pt.textContent = saved._phoneText;
      pt.style.color = '#000';
    }
    saved = null;
  }

  /* Save fields when variant picker changes */
  document.addEventListener('change', function (e) {
    if (!e.target.closest('variant-picker')) return;
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'SELECT') return;
    saveFields();
  }, true);

  /* Restore fields after Horizon dispatches variant events */
  var _dispatch = EventTarget.prototype.dispatchEvent;
  EventTarget.prototype.dispatchEvent = function (event) {
    var result = _dispatch.call(this, event);
    if (saved && event.type && event.type.indexOf('variant') !== -1) {
      requestAnimationFrame(restoreFields);
    }
    return result;
  };

  /* ═══════════════════════════════════════════════════
   * PRICE ON ADD-TO-CART BUTTON
   * Shows "Ajouter au panier — XX,XXX CFA" dynamically
   * ═══════════════════════════════════════════════════ */
  function updateButtonPrice() {
    var priceEl = document.querySelector('product-price .price');
    var btn = document.querySelector('.add-to-cart-button .add-to-cart-text__content');
    if (!btn) return;
    var innerSpan = btn.querySelector('span > span');
    if (!innerSpan) innerSpan = btn.querySelector('span');
    if (!innerSpan) return;
    var price = '';
    if (priceEl) price = priceEl.textContent.trim();
    var base = 'Ajouter au panier';
    if (price) {
      innerSpan.textContent = base + '  —  ' + price;
    } else {
      innerSpan.textContent = base;
    }
  }

  /* Run on load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(updateButtonPrice, 100); });
  } else {
    setTimeout(updateButtonPrice, 100);
  }

  /* Run on variant change via MutationObserver on product-price */
  function observePrice() {
    var target = document.querySelector('product-price, .price');
    if (!target) return;
    var obs = new MutationObserver(function () { setTimeout(updateButtonPrice, 50); });
    obs.observe(target, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observePrice);
  } else {
    observePrice();
  }
})();
